import {Injectable} from "@angular/core";
import {
    IPortfolioResultResponse,
    IPortfolioResultRequest,
    IPortfolioResultACCRebal,
    IPortfolioResultAccount,
    IPortfolioResultPFSECRebal,
    IPortfolioResultUnheldSecurity,
    IPortfolioResultUnheldSecurityDetailRequest,
    IPortfolioResultSECRebal,
    IPortfolioResultDialogData
} from "@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model";
import {PrValidationService} from "@irb/irb-editors/editor-portfolio-result/services/pr-validation.service";
import {UtilityService} from '@irb-common/services/utility.service';
import {IDropdownItem} from '@irb-common/models/dropdown.model';

@Injectable({
    providedIn: "root"
})
export class PrUnheldSecurityService {
    constructor(private prvs: PrValidationService,
                private util: UtilityService) {
    }

    populateUnheldSecurityAccountOptions(data: IPortfolioResultResponse): Array<IDropdownItem> {
        const a: Array<IDropdownItem> = [];

        data.accRebalDTOList.forEach((item: IPortfolioResultACCRebal) => {
            a.push({
                name: item.accountNum + "&nbsp" + "&nbsp" + "&nbsp" + "&nbsp" + "&nbsp" +  item.accountName + "&nbsp" +  "&nbsp" + "&nbsp" + "&nbsp" + "&nbsp" + item.accountLocationType,
                value: item.accountNum
            } as IDropdownItem);
        });

        return a;
    }

    addSecurityToOrders(data: IPortfolioResultResponse, unheldSecurityObj: IPortfolioResultUnheldSecurity, cbFunc: Function) {
        let itemisPresent = false;
        let allCashAcc = true;

        data.secRebalDTOList.forEach((item: IPortfolioResultSECRebal) => {
            if (item.accountNum === unheldSecurityObj.accountNum) {
                allCashAcc = false;
                unheldSecurityObj.secAccId = item.secAccId;
                unheldSecurityObj.accountName = item.accountName;
                unheldSecurityObj.accountNum = item.accountNum;
                unheldSecurityObj.accountType = item.accountType;
                unheldSecurityObj.capGainsReinvest = item.capGainsReinvest;
                unheldSecurityObj.dividendReinvest = item.dividendReinvest;
                unheldSecurityObj.accountLocationType = item.accountLocationType;

                if (item.securityId === unheldSecurityObj.securityId) {
                    itemisPresent = true;
                }
            }
        });

        if (allCashAcc) {
            data.accRebalDTOList.forEach((item: IPortfolioResultACCRebal) => {
                if (item.accountNum === unheldSecurityObj.accountNum) {
                    unheldSecurityObj.secAccId = item.accId;
                    unheldSecurityObj.accountName = item.accountName;
                    unheldSecurityObj.accountNum = item.accountNum;
                    unheldSecurityObj.accountType = (item.accountType === "TAXABLE") ? "T" : "Q";
                }
            });
        }

        if (itemisPresent) {
            const dialogData = {} as IPortfolioResultDialogData;
            dialogData.title = "Warning";
            dialogData.message = unheldSecurityObj.secSymbol + " is already held in the account you selected. To buy more of it, toggle the proposed orders section view to show all positions and enter a buy in the row showing the existing holding of " + unheldSecurityObj.secSymbol + ".";
            dialogData.isError = true;
            this.prvs.displayPfSummryDialog(data, dialogData);
            cbFunc(false);
            return;
        }

        const newItem = unheldSecurityObj as IPortfolioResultSECRebal;
        newItem.buyUnheldSecurity = true;
        newItem.lotRebalanceSummaryDTOList = [];

        const snapShotItem = this.util.clone(newItem);

        this.prvs.checkValidation({
            data: data, 
            item: newItem, 
            snapShotItem: snapShotItem, 
            validnFor: 'sellOrBuyMin', 
            onComplete: (callStack, item) => {
                this.prvs.checkValidation({
                    data: data, 
                    item: item, 
                    snapShotItem: snapShotItem, 
                    validnFor: 'equityTradeMinimum', 
                    onComplete: (_callStack, validatedItem) => {
                        data.secRebalDTOList.push(validatedItem);
                        cbFunc(true, validatedItem);
                    },
                    callStack: callStack
                })
            }
        });
    }

    addSecurityToAllocations(data: IPortfolioResultResponse, unheldSecurityObj: IPortfolioResultUnheldSecurity, cbFunc: Function) {
        let itemIsPresent = false;

        data.pfSecRebalDTOList.forEach((item: IPortfolioResultPFSECRebal) => {
            if (item.secId === unheldSecurityObj.securityId) {
                itemIsPresent = true;
            }
        });

        if (!itemIsPresent) {
            const newItem = {
                secId: unheldSecurityObj.securityId,
                secSymb: unheldSecurityObj.secSymbol,
                secDescription: unheldSecurityObj.secDescription,
                secCatagory: unheldSecurityObj.secCatagory,
                secType: unheldSecurityObj.secType,
                secAdjAmt: unheldSecurityObj.txnAmt,
                secModelTargetPercnt: unheldSecurityObj.modelTargetPercnt,
                secMinTolPercnt: unheldSecurityObj.minTolPercnt,
                secMaxTolPercnt: unheldSecurityObj.maxTolPercnt,
                secMktVal: 0,
                secCurPercntVal: 0,
                secRelVariance: unheldSecurityObj.modelTargetPercnt !== 0 ? -100 : 0, //rel variance = (abs variance)*100/target
                secAbsVariance: unheldSecurityObj.modelTargetPercnt !== 0 ? -unheldSecurityObj.modelTargetPercnt : 0, //abs variance = (cur%-target%)
                secMktValAfterAdj: 0,
                secPercntValAfterAdj: 0,
                secRelVarianceAfterAdj: 0,
                secAbsVarianceAfterAdj: 0,
                isBuyUnheldSecurity: true
            } as IPortfolioResultPFSECRebal;

            data.pfSecRebalDTOList.push(newItem);
        }
        cbFunc();
    }

    getUnheldSecDetailsRequest(req: IPortfolioResultUnheldSecurityDetailRequest, data: IPortfolioResultResponse) {
        const sendData = {} as IPortfolioResultRequest;

        let allCashAcc = true;
        sendData.method = "getUnheldSecDetails";
        sendData.portfolioId = data.portfolioId;
        sendData.rebalId = data.rebalId;
        sendData.pfRebalResKeyId = data.pfRebalResKeyId; //Model history change
        sendData.unheldSecurity = {} as IPortfolioResultUnheldSecurity;
        sendData.unheldSecurity.securityId = req.securityId;
        sendData.unheldSecurity.secType = req.secType;
        sendData.unheldSecurity.txnAmt = req.txnCurrency === "DOLLARS" ? req.txnAmt : 0;
        sendData.unheldSecurity.txnShares = req.txnCurrency === "SHARES" ? req.txnShares : 0;
        sendData.unheldSecurity.txnType = "BUY";
        sendData.unheldSecurity.rebalSecId = new Date().getTime() * -1;
        data.userEdit = true;
        sendData.userEdit = true;
        sendData.unheldSecurity.txnCurrency = req.txnCurrency;

        data.secRebalDTOList.forEach((item: IPortfolioResultSECRebal) => {
            if (item.accountNum === req.accountNum) {
                allCashAcc = false;
                sendData.unheldSecurity.secAccId = item.secAccId;
                sendData.unheldSecurity.accountName = item.accountName;
                sendData.unheldSecurity.accountNum = item.accountNum;
                sendData.unheldSecurity.accountType = item.accountType;
                sendData.unheldSecurity.capGainsReinvest = item.capGainsReinvest;
                sendData.unheldSecurity.dividendReinvest = item.dividendReinvest;
                sendData.unheldSecurity.accountLocationType = item.accountLocationType;
            }
        });

        data.accRebalDTOList.forEach((item: IPortfolioResultACCRebal) => {
            if (item.accountNum === req.accountNum) {
                sendData.unheldSecurity.secAccId = item.accId;
                sendData.unheldSecurity.accountName = item.accountName;
                sendData.unheldSecurity.accountNum = item.accountNum;
                sendData.unheldSecurity.accountType = item.accountType === "TAXABLE" ? "T" : "Q";
            }
        });

        return sendData;
    }
}
